import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import type {
  ChatMessage,
  ClarificationCardOption,
  ChatSessionListItem,
  FilterType,
  GraphData,
  ImageReference,
  MessageFeedbackValue,
  NoteSaveState,
  RightSidebarView,
  RetrievalApiRequest,
  RetrievalSource,
  SourceNode,
  SourceNodeType,
  SourcePanelState,
  StepTrace,
  StreamEvent,
  TalkToDataNoteDetail,
  TalkToDataNoteSummary,
} from './types';
import type { MachineContext } from '@/components/chat/SessionContextIndicator';
import { extractStructuredSourceCitation } from './sourceCitation';
import { resolveSourceTarget } from './utils/resolveSourceTarget';
import { matchDemoResponse } from './demoData';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import { useWorkspaceMemberStore } from '@/stores/useWorkspaceMemberStore';
import { getRuntimeAccessToken, withAuthHeaders } from '@/auth/runtimeToken';
import { extractCitationIndices, normalizeCitationGroups, CITATION_RE } from './citationUtils';

const RETRIEVAL_STREAM_URL = '/api/python/retrieval/query/stream';
const RETRIEVAL_MACHINE_CONTEXT_API = '/api/python/retrieval/session';
const TALK_TO_DATA_NOTES_API = '/api/dotnet/talk-to-data/notes';
const LANGFUSE_SCORE_API = '/api/dotnet/langfuse/scores';
const TALK_TO_DATA_DEBUG_ENABLED = import.meta.env.DEV;

// ─── State shape ─────────────────────────────────────────────────────────────

interface QueryState {
  /** All chat messages (user + assistant) */
  messages: ChatMessage[];
  /** Current knowledge-graph data to display */
  graphData: GraphData;
  /** Workspace scope the persisted chat state belongs to. */
  boundWorkspaceId: string | null;
  /** Currently highlighted node in the graph */
  selectedNodeId: string | null;
  /** Active source-type filter */
  filter: FilterType;
  /** Whether an answer is being fetched / streamed */
  isLoading: boolean;
  /** Pipeline debug mode — sends debug=true to backend and shows trace panel */
  debugMode: boolean;
  /** Active AbortController for the current SSE request — null when idle */
  _abortController: AbortController | null;
  /** Trace steps accumulated during the current streaming request */
  _pendingTraceSteps: StepTrace[];
  /** Sources accumulated during the current streaming request */
  _pendingSources: RetrievalSource[];
  /** Image references accumulated during the current streaming request (Task 14.c) */
  _pendingImages: ImageReference[];

  // ── Session persistence ──────────────────────────────────────────────────
  /** Server-side session ID for the active chat (null = not yet persisted) */
  sessionId: string | null;
  /** List of saved sessions for the sidebar */
  sessions: ChatSessionListItem[];
  /** Whether a checkpoint restore is in progress */
  isRestoring: boolean;
  /** Whether an undo-last-turn is in progress */
  isUndoing: boolean;
  /** Saved private notes for the current workspace/user */
  notes: TalkToDataNoteSummary[];
  /** Currently selected saved note ID */
  selectedNoteId: string | null;
  /** Loaded note detail currently shown in the right sidebar */
  selectedNote: TalkToDataNoteDetail | null;
  /** Loading state for note list and detail operations */
  notesLoading: boolean;
  /** Per-message save feedback state for note creation */
  noteSaveStates: Record<string, NoteSaveState>;
  /** Current right sidebar mode */
  rightSidebarView: RightSidebarView;
  /** Center panel state: graph view or inline source viewer */
  sourcePanel: SourcePanelState;
  /** Aktiver Maschinenkontext für die laufende Chat-Session (null = kein Kontext) */
  machineContext: MachineContext | null;
  /** Retrieval-Modus: 'precise' (nur ontologie-konforme Quellen) | 'comprehensive' (alle + Penalty) */
  retrievalMode: 'precise' | 'comprehensive';
  /** Ob der Maschinenkontext über mehrere Fragen erhalten bleibt (User-Präferenz) */
  sessionContextRetention: boolean;
  /**
   * Letzte Prompt-Injection-Warnung aus dem Backend (DevPlan 0103.07.a).
   * Wird beim `injection_warning`-SSE-Event aus dem Retrieval-Stream gefüllt
   * und vom `UntrustedContentWarning`-Toast angezeigt. Bleibt nach Stream-Ende
   * bestehen, bis der User sie wegklickt oder eine neue Query startet.
   */
  injectionWarning: {
    chunkId: string;
    riskLevel: 'medium' | 'high' | 'critical';
    patternsMatched: string[];
    action: 'drop' | 'wrap' | 'warn';
  } | null;
  /**
   * Fail-Closed-Block des PolicyEnforcers (DevPlan 0103.07.a + 0103.07.d).
   * Wird gesetzt, wenn der Retrieval-Stream mit HTTP 503 abbricht (Entra-
   * Resolver-Ausfall + Stale-Cache abgelaufen). Vom `RetrievalBlockedBanner`
   * (Admin-Surface) angezeigt.
   */
  retrievalBlocked: {
    reason: string;
    statusCode: number;
    occurredAt: string;
  } | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  sendQuestion: (question: string) => Promise<void>;
  /** Abort the current streaming request and keep any partial answer already received. */
  stopStreaming: () => void;
  /** Expand 1-hop neighborhood of an entity node and merge into graphData. */
  drillDown: (uid: string) => Promise<void>;
  setFilter: (filter: FilterType) => void;
  selectNode: (id: string | null) => void;
  /** Open a source document inline in the center panel. Falls back to selecting the graph node if no documentId is resolvable. */
  openSourceInline: (source: RetrievalSource) => void;
  /** Return the center panel to graph mode. */
  closeSourceInline: () => void;
  toggleDebugMode: () => void;
  /** Wird vom `UntrustedContentWarning`-Toast aufgerufen, sobald der User die Warnung weggeklickt hat. */
  dismissInjectionWarning: () => void;
  /** Wird vom `RetrievalBlockedBanner` aufgerufen, wenn der Admin den Banner wegklickt. */
  dismissRetrievalBlocked: () => void;
  reset: () => void;
  /** Setzt den aktiven Maschinenkontext (nach Klärungsauswahl). */
  setMachineContext: (context: MachineContext | null) => void;
  clearMachineContext: () => Promise<void>;
  setRetrievalMode: (mode: 'precise' | 'comprehensive') => void;
  /** Setzt Session-Kontext-Retention. Bei false wird machineContext geleert. */
  setSessionContextRetention: (retained: boolean) => void;
  /**
   * Wird vom ClarificationCardMessage-Handler aufgerufen wenn der Nutzer
   * eine Maschine auswählt. Speichert den Kontext lokal und sendet die
   * ursprüngliche Frage erneut mit machine_id im Filter.
   */
  handleMachineSelection: (option: ClarificationCardOption, sessionId?: string) => Promise<void>;

  // ── Session actions ──────────────────────────────────────────────────────
  /** Load the list of chat sessions for a workspace. */
  loadSessionList: (workspaceId: string) => Promise<void>;
  /** Rebinds the store to the active workspace and clears stale persisted state when it changes. */
  bindWorkspace: (workspaceId: string | null) => void;
  /** Load a full session (messages + graph) from the server. */
  loadSession: (sessionId: string) => Promise<void>;
  /** Start a new chat (clears state, optionally creates server session). */
  newSession: () => void;
  /** Delete a session from the server and remove from list. */
  deleteSession: (sessionId: string) => Promise<void>;
  /** Load saved notes for the current workspace/user. */
  loadNotes: (workspaceId: string) => Promise<void>;
  /** Save an assistant answer as a private note. */
  saveNoteFromMessage: (messageId: string) => Promise<void>;
  /** Load and display a note in detail view. */
  selectNote: (noteId: string) => Promise<void>;
  /** Return from detail to the note list. */
  closeNoteDetail: () => void;
  /** Delete a saved note. */
  deleteNote: (noteId: string) => Promise<void>;
  /** Restore conversation to a checkpoint message — deletes everything after it. */
  restoreCheckpoint: (messageId: string) => Promise<void>;
  /** Undo the last user→assistant turn. */
  undoLastTurn: () => Promise<void>;
  /** Submit thumbs feedback for a persisted assistant answer. */
  submitMessageFeedback: (messageId: string, value: MessageFeedbackValue) => Promise<void>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 10);
}

/** Narrowed shape of the SSE `clarification` event (DevPlan 0145). */
type ClarificationStreamEvent = Extract<StreamEvent, { type: 'clarification' }>;

/**
 * Baut die Clarification-`ChatMessage` aus einem `clarification`-SSE-Event
 * (DevPlan 0145). Flaches Modell: die Optionen sind flach (`cascade_type`
 * 'model' ODER 'series'), es gibt keine mehrstufige Kaskade mehr. Daher ein
 * generischer Text statt Kaskaden-Wortlaut, `cascade_type: undefined` und
 * `presentation` aus dem Event (Default 'cards'). Beide Clarification-Pfade
 * (Haupt-`query` + Folge-`handleMachineSelection`) nutzen denselben Builder,
 * damit sie nicht erneut divergieren.
 */
function buildClarificationMessage(
  event: ClarificationStreamEvent,
  sessionId: string | undefined,
): ChatMessage {
  return {
    id: makeId(),
    role: 'assistant',
    text: 'Zu welcher Maschine oder Serie? Du kannst auswählen oder es einfach im Chat nennen.',
    timestamp: new Date(),
    message_type: 'clarification_cards',
    clarification_options: event.options,
    session_id: sessionId,
    clarification_level: 0,
    presentation: event.presentation ?? 'cards',
    cascade_type: undefined,
  };
}

function updateMessageFeedbackState(
  messages: ChatMessage[],
  messageId: string,
  value: MessageFeedbackValue,
  state: ChatMessage['feedbackState'],
  submittedAt?: Date,
): ChatMessage[] {
  return messages.map((message) =>
    message.id === messageId
      ? {
          ...message,
          feedbackValue: value,
          feedbackState: state,
          ...(submittedAt ? { feedbackSubmittedAt: submittedAt } : {}),
        }
      : message,
  );
}

/**
 * Classify a RetrievalSource into a SourceNodeType.
 *
 * Rules:
 * - metadata.source_type = 'interview_report' | 'interview_source' → 'expert'
 * - chunk / document (uploaded files) → 'document' (or 'image' if type says so)
 * - entity with person/expert/organization/interview/report type → 'expert' (Reports)
 * - entity with regulation/norm/standard/rule type → 'norm'
 * - default → 'document'
 */
function classifySource(s: { source_kind?: string; type?: string; metadata?: Record<string, unknown> | null }): SourceNodeType {
  const kind = (s.source_kind ?? '').toLowerCase();
  const type = (s.type ?? '').toLowerCase();
  const sourceType = (String(s.metadata?.source_type ?? '')).toLowerCase();
  const docType = ((s.metadata?.document_type as string | undefined) ?? '').toLowerCase();
  const docName = ((s.metadata?.document_name as string | undefined) ?? '').toLowerCase();

  // Interview reports/sources — check in priority order:
  // 1. source_type in Qdrant metadata (new chunks)
  // 2. document_type in Qdrant metadata (chunks indexed before source_type was added)
  // 3. document_name prefix (very old chunks, set by report_indexer title format)
  if (sourceType === 'interview_report' || sourceType === 'interview_source') return 'expert';
  if (docType === 'interviewreport' || docType === 'interviewsource') return 'expert';
  if (docName.startsWith('interview-report')) return 'expert';

  // Trust the backend's already-resolved type when it's a known classification.
  // _resolve_source_type() on the backend already checked metadata; avoid re-classifying
  // to 'document' just because metadata isn't mirrored on the frontend.
  if (type === 'expert') return 'expert';
  if (type === 'norm') return 'norm';

  // Image chunks from Qdrant — modality field or image_ref key signals an image chunk
  if (s.metadata?.modality === 'image' || s.metadata?.image_ref) {
    return 'image';
  }

  if (kind === 'chunk' || kind === 'document') {
    if (type.includes('image') || type.includes('img') || type.includes('photo')) return 'image';
    return 'document';
  }

  if (kind === 'entity') {
    if (
      type.includes('person') || type.includes('expert') || type.includes('organization') ||
      type.includes('contact') || type.includes('interview') || type.includes('report')
    ) return 'expert';
    if (
      type.includes('regulation') || type.includes('norm') || type.includes('standard') ||
      type.includes('rule')
    ) return 'norm';
    if (type.includes('image') || type.includes('img')) return 'image';
  }

  return 'document';
}

/**
 * Parse an SSE stream from a fetch Response.body, yielding parsed StreamEvent
 * objects for each `data: ...` line.
 */
async function* parseSSE(body: ReadableStream<Uint8Array>): AsyncGenerator<StreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const json = line.slice(6).trim();
        if (!json) continue;
        try {
          const event = JSON.parse(json) as StreamEvent;
          yield event;
        } catch {
          // ignore malformed SSE lines
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ─── Graph rebuild helper ────────────────────────────────────────────────────

/**
 * Rebuild the GraphData from an assistant message's text and sources list.
 * Extracts cited [[N]] references and builds the question→source graph.
 */
function rebuildGraphFromSources(text: string, sources: RetrievalSource[]): GraphData {
  const citedIndices = new Set<number>(extractCitationIndices(text));
  const sourcesToShow = citedIndices.size > 0
    ? [...citedIndices].sort((a, b) => a - b).map((n) => sources[n - 1]).filter(Boolean)
    : sources.slice(0, 8);

  const questionNode: SourceNode = { id: 'q', label: '?', type: 'question', score: 1.0 };
  const srcNodes: SourceNode[] = sourcesToShow.map((s) => ({
    id: s.uid,
    label: s.name,
    type: classifySource(s),
    score: s.confidence ?? 0.5,
    source: extractStructuredSourceCitation(s).documentName ?? s.source_ref ?? undefined,
    detail: s.text ?? undefined,
    metadata: s.metadata ?? undefined,
  }));
  const edges = srcNodes.map((n) => ({ from: 'q', to: n.id }));

  return { nodes: [questionNode, ...srcNodes], edges };
}

function buildNoteSummary(note: TalkToDataNoteDetail): TalkToDataNoteSummary {
  return {
    id: note.id,
    title: note.title,
    questionPreview: note.questionText.length <= 160 ? note.questionText : `${note.questionText.slice(0, 157)}...`,
    citationCount: note.citations.length,
    sourceCount: note.sources.length,
    imageCount: note.images.length,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
  };
}

function toOptionalGuid(value: string | null | undefined): string | null {
  if (!value) return null;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? value : null;
}

function getCurrentUserId(): string | null {
  const orgMemberUserId = useOrgMemberStore.getState().currentUser?.userId ?? null;
  if (orgMemberUserId) return orgMemberUserId;

  // Fallback kept for tests and non-MSAL contexts.
  return useAuthStore.getState().user?.id ?? null;
}

function getCurrentUserRole(): string {
  const currentUser = useOrgMemberStore.getState().currentUser;
  if (currentUser?.userId) {
    const member = useWorkspaceMemberStore.getState().members.find(
      (m) => m.userId === currentUser.userId,
    );
    if (member?.role) return member.role;
  }
  return currentUser?.role ?? 'Member';
}

async function fetchProtectedDotNet(url: string, init: RequestInit = {}): Promise<Response | null> {
  const token = await getRuntimeAccessToken();
  if (!token) {
    logger.warn('auth', 'Missing access token for protected .NET request', url);
    return null;
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);

  return fetch(url, {
    ...init,
    headers,
  });
}

// ─── Auto-save helper ────────────────────────────────────────────────────────

const CHAT_SESSION_API = '/api/dotnet/chatsessions';

/**
 * Persist the current user message + assistant answer to the server.
 * Creates a session on first save, then adds messages in batch.
 * Runs fire-and-forget after stream completion.
 */
async function autoSaveMessages(
  userMsg: ChatMessage,
  assistantMsg: ChatMessage,
  graphData: GraphData,
  getState: () => QueryState,
  setState: (partial: Partial<QueryState>) => void,
) {
  const workspace = useWorkspaceStore.getState().activeWorkspace;
  if (!workspace) return; // No workspace → demo mode, skip save

  const userId = getCurrentUserId();
  if (!userId) return; // No user → skip save

  try {
    let sessionId = getState().sessionId;
    let createdSessionTitle: string | null = null;
    let createdSessionAt: string | null = null;

    // Create session if first save
    if (!sessionId) {
      const title = userMsg.text.length > 100 ? userMsg.text.slice(0, 100) + '…' : userMsg.text;
      const createRes = await fetchProtectedDotNet(`${CHAT_SESSION_API}/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspaceId: workspace.id,
          userId,
          title,
        }),
      });
      if (!createRes) return;
      if (!createRes.ok) { logger.warn('chat', 'Auto-save: create session failed', createRes.status); return; }
      const created = await createRes.json() as { id: string; title?: string; createdAt?: string; updatedAt?: string; messageCount?: number };
      sessionId = created.id;
      createdSessionTitle = created.title ?? title;
      createdSessionAt = created.updatedAt ?? created.createdAt ?? new Date().toISOString();
      setState({ sessionId });
    }

    // Batch-add both messages
    const messageBatch = [
      {
        messageId: userMsg.id,
        role: 'user',
        content: userMsg.text,
        sourceCount: 0,
      },
      {
        messageId: assistantMsg.id,
        role: 'assistant',
        content: assistantMsg.text,
        sourceCount: assistantMsg.sourceCount ?? 0,
        sourcesJson: assistantMsg.sources ? JSON.stringify(assistantMsg.sources) : null,
        graphDataJson: JSON.stringify(graphData),
        imagesJson: assistantMsg.images ? JSON.stringify(assistantMsg.images) : null,
        debugTraceJson: assistantMsg.debugTrace ? JSON.stringify(assistantMsg.debugTrace) : null,
        langfuseTraceId: assistantMsg.langfuseTraceId ?? null,
        langfuseObservationId: assistantMsg.langfuseObservationId ?? null,
      },
    ];

    await fetchProtectedDotNet(`${CHAT_SESSION_API}/sessions/${sessionId}/messages/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(messageBatch),
    });

    const nextUpdatedAt = new Date().toISOString();
    setState({
      sessions: (() => {
        const existing = getState().sessions;
        const existingIndex = existing.findIndex((session) => session.id === sessionId);
        if (existingIndex >= 0) {
          return existing
            .map((session) => session.id === sessionId
              ? {
                  ...session,
                  title: createdSessionTitle ?? session.title,
                  messageCount: session.messageCount + 2,
                  updatedAt: nextUpdatedAt,
                }
              : session)
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        }

        return [
          {
            id: sessionId!,
            title: createdSessionTitle ?? (userMsg.text.length > 100 ? userMsg.text.slice(0, 100) + '…' : userMsg.text),
            messageCount: 2,
            createdAt: createdSessionAt ?? nextUpdatedAt,
            updatedAt: nextUpdatedAt,
          },
          ...existing,
        ];
      })(),
    });
  } catch (err) {
    logger.warn('chat', 'Auto-save failed', err);
  }
}

async function loadMachineContextForSession(sessionId: string): Promise<MachineContext | null> {
  try {
    const response = await fetch(
      `${RETRIEVAL_MACHINE_CONTEXT_API}/${encodeURIComponent(sessionId)}/machine-context`,
      { headers: await withAuthHeaders() },
    );
    if (!response.ok) return null;
    const payload = await response.json() as { context?: { machine_id?: string; machine_name?: string } | null };
    const context = payload.context;
    if (!context?.machine_id) return null;
    return {
      machineId: context.machine_id,
      machineName: context.machine_name || context.machine_id,
    };
  } catch {
    return null;
  }
}

async function persistMachineContextForSession(sessionId: string, context: MachineContext): Promise<void> {
  await fetch(`${RETRIEVAL_MACHINE_CONTEXT_API}/${encodeURIComponent(sessionId)}/machine-context`, {
    method: 'PUT',
    headers: await withAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ machine_id: context.machineId, machine_name: context.machineName }),
  });
}

async function clearMachineContextForSession(sessionId: string): Promise<void> {
  await fetch(`${RETRIEVAL_MACHINE_CONTEXT_API}/${encodeURIComponent(sessionId)}/machine-context`, {
    method: 'DELETE',
    headers: await withAuthHeaders(),
  });
}

// ─── Session-Storage adapter with Date revival ──────────────────────────────

const sessionStorageAdapter = {
  getItem: (name: string) => {
    const str = sessionStorage.getItem(name);
    if (!str) return null;
    // Revive ISO-8601 strings back into Date objects (for message timestamps)
    return JSON.parse(str, (_key, value) => {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) return d;
      }
      return value;
    });
  },
  setItem: (name: string, value: unknown) => {
    sessionStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name: string) => {
    sessionStorage.removeItem(name);
  },
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useQueryStore = create<QueryState>()(persist((set, get) => ({
  messages: [],
  graphData: { nodes: [], edges: [] },
  boundWorkspaceId: null,
  selectedNodeId: null,
  filter: 'all',
  isLoading: false,
  debugMode: false,
  _abortController: null,
  _pendingTraceSteps: [],
  _pendingSources: [],
  _pendingImages: [],
  sessionId: null,
  sessions: [],
  isRestoring: false,
  isUndoing: false,
  notes: [],
  selectedNoteId: null,
  selectedNote: null,
  notesLoading: false,
  noteSaveStates: {},
  rightSidebarView: 'notes-list',
  sourcePanel: { mode: 'graph', target: null },
  machineContext: null,
  injectionWarning: null,
  retrievalBlocked: null,
  retrievalMode: 'comprehensive',
  sessionContextRetention: true,

  sendQuestion: async (question: string) => {
    // Snapshot history BEFORE adding the new user message (max 10 previous turns)
    const prevMessages = get().messages;
    const conversationHistory = prevMessages
      .filter((m) => m.role !== 'system')
      .slice(-10)
      .map((m) => ({ role: m.role, text: m.text }));

    // Add user message + start loading. Alte Security-Warnungen aus der
    // vorherigen Antwort werden bei jeder neuen Query geleert (0103.07.a).
    set((s) => ({
      messages: [
        ...s.messages,
        { id: makeId(), role: 'user', text: question, timestamp: new Date() },
      ],
      isLoading: true,
      selectedNodeId: null,
      rightSidebarView: s.selectedNote ? 'note-detail' : 'notes-list',
      sourcePanel: { mode: 'graph', target: null },
      injectionWarning: null,
      retrievalBlocked: null,
    }));

    const workspace = useWorkspaceStore.getState().activeWorkspace;

    // ── No workspace → instant demo fallback ───────────────────────────────
    if (!workspace) {
      await new Promise<void>((r) => setTimeout(r, 900));
      const demo = matchDemoResponse(question);
      set((s) => ({
        messages: [
          ...s.messages,
          {
            id: makeId(),
            role: 'assistant',
            text: demo.answer,
            sourceCount: demo.nodes.filter((n) => n.type !== 'question').length,
            timestamp: new Date(),
          },
        ],
        graphData: { nodes: demo.nodes, edges: demo.edges },
        isLoading: false,
      }));
      return;
    }

    // ── Live SSE streaming ─────────────────────────────────────────────────
    const debugMode = TALK_TO_DATA_DEBUG_ENABLED ? get().debugMode : false;
    const abortController = new AbortController();
    set({ _abortController: abortController });
    const { sessionId: currentSessionId, retrievalMode } = get();
    const activeMachineContext = get().machineContext;

    const machineFilters = activeMachineContext
      ? { machine_id: activeMachineContext.machineId, machine_name: activeMachineContext.machineName }
      : {};
    const retrievalModeFilter = retrievalMode === 'precise'
      ? { retrieval_mode: 'precise' as const }
      : {};
    const hasFilters = activeMachineContext !== null || retrievalMode === 'precise';

    const body: RetrievalApiRequest = {
      question,
      workspace_id: workspace.id,
      domain_id: workspace.domain?.id,
      conversation_history: conversationHistory,
      workspace_role: getCurrentUserRole(),
      ...(debugMode && { debug: true }),
      ...(currentSessionId && { session_id: currentSessionId }),
      ...(hasFilters && { filters: { ...machineFilters, ...retrievalModeFilter } }),
    };

    let accText = '';
    let sourceCount = 0;
    let doneMetadata: Extract<StreamEvent, { type: 'done' }>['metadata'] = undefined;
    let streamPiiRedacted = false;
    let dlpAction: 'redacted' | 'blocked' | undefined;
    const assistantMessageId = makeId();
    // Reset pending trace steps, sources, and images for this new request
    set((s) => ({
      messages: [
        ...s.messages,
        { id: assistantMessageId, role: 'assistant', text: '', sourceCount: 0, timestamp: new Date() },
      ],
      _pendingTraceSteps: [],
      _pendingSources: [],
      _pendingImages: [],
    }));

    try {
      const response = await fetch(RETRIEVAL_STREAM_URL, {
        method: 'POST',
        headers: await withAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        // 0103.07.a: HTTP 503 = PolicyEnforcer fail-closed (Entra-Directory-
        // Ausfall + Stale-Cache abgelaufen). Wir merken uns das im
        // retrievalBlocked-State, damit der `RetrievalBlockedBanner` reagieren
        // kann. Andere Status-Codes bleiben generischer Error.
        if (response.status === 503) {
          let detail = 'Retrieval blockiert (Fail-Closed). Group-Verzeichnis nicht erreichbar.';
          try {
            const errorBody = await response.clone().json();
            if (errorBody?.detail?.message) detail = errorBody.detail.message;
          } catch {
            // Kein JSON-Body — Default-Reason behalten
          }
          set({
            retrievalBlocked: {
              reason: detail,
              statusCode: 503,
              occurredAt: new Date().toISOString(),
            },
          });
        }
        throw new Error(`HTTP ${response.status}`);
      }

      for await (const event of parseSSE(response.body)) {
        if (event.type === 'token') {
          accText += event.content;
          set((s) => ({
            messages: s.messages.map((message) => (
              message.id === assistantMessageId
                ? { ...message, text: accText }
                : message
            )),
          }));

        } else if (event.type === 'answer_replace') {
          // Progressive Streaming + nachgelagerte DLP: der live gestreamte
          // Rohtext wird durch die redigierte/blockierte Version ERSETZT.
          accText = event.content;
          set((s) => ({
            messages: s.messages.map((message) => (
              message.id === assistantMessageId
                ? { ...message, text: accText }
                : message
            )),
          }));

        } else if (event.type === 'sources') {
          const questionNode: SourceNode = {
            id: 'q',
            label: question,
            type: 'question',
            score: 1.0,
          };
          const srcNodes: SourceNode[] = event.sources.map((s) => ({
            id: s.uid,
            label: s.name,
            type: classifySource(s),
            score: s.confidence ?? 0.5,
            source: s.source_ref ?? undefined,
            detail: s.text ?? undefined,
            metadata: s.metadata ?? undefined,
          }));
          sourceCount = srcNodes.length;
          const srcEdges = srcNodes.map((n) => ({ from: 'q', to: n.id }));
          // Keep ordered sources list for [[N]] citation rendering
          set({
            graphData: { nodes: [questionNode, ...srcNodes], edges: srcEdges },
            _pendingSources: event.sources,
          });

        } else if (event.type === 'graph') {
          // Graph context nodes (Neo4j entity hops) are intentionally ignored here.
          // The graph is rebuilt after streaming to show only cited sources.

        } else if (event.type === 'images') {
          // Accumulate image references for [[IMG:N]] rendering (Task 14.c)
          set({ _pendingImages: event.images });

        } else if (event.type === 'debug_trace') {
          // Accumulate trace steps during streaming
          set((s) => ({
            _pendingTraceSteps: [...s._pendingTraceSteps, event.step],
          }));

        } else if (event.type === 'clarification') {
          // Backend requests machine disambiguation (DevPlan 0145). Flaches
          // Modell — Builder shared mit handleMachineSelection (kein Kaskaden-Text,
          // presentation Default 'cards', cascade_type undefined).
          const clarificationMsg = buildClarificationMessage(event, currentSessionId ?? undefined);
          set((s) => ({
            messages: [
              ...s.messages.filter((message) => message.id !== assistantMessageId),
              clarificationMsg,
            ],
            isLoading: false,
            _abortController: null,
          }));
          return; // Kein normales stream-Ende — auf Nutzerauswahl warten

        } else if (event.type === 'error') {
          logger.warn('retrieval', 'SSE error event', { message: event.message, phase: event.phase ?? '' });
        } else if (event.type === 'machine_context') {
          // Backend auto-selected a machine during disambiguation — update frontend context
          const ctx = { machineId: event.machine_id, machineName: event.machine_name };
          set({ machineContext: ctx });
          if (currentSessionId) {
            persistMachineContextForSession(currentSessionId, ctx).catch(() => {});
          }
        } else if (event.type === 'pii') {
          // PII-Redaction event (0101.06) — capture flag for the final assistantMsg.
          if (event.pii_redacted) {
            streamPiiRedacted = true;
          }
        } else if (event.type === 'dlp_violation') {
          // DLP output filter violation event (0101.07) — capture action for assistantMsg.
          dlpAction = event.action_taken;
        } else if (event.type === 'injection_warning') {
          // 0103.07.a: Prompt-Injection-Warn-Event aus Backend
          // (InjectionWarningStreamEvent). UntrustedContentWarning-Toast
          // zeigt das im UI; im Store wird die ZULETZT empfangene Warnung
          // gehalten — eine pro Antwort ist Konvention.
          set({
            injectionWarning: {
              chunkId: event.chunk_id,
              riskLevel: event.risk_level,
              patternsMatched: event.patterns_matched ?? [],
              action: event.action,
            },
          });
        } else if (event.type === 'done') {
          doneMetadata = event.metadata;
        }
        // 'done' is handled implicitly — stream ends after it
      }

      // Stream finished — commit final answer and rebuild graph to show only cited sources
      const finalText = accText.trim() || 'Keine Antwort erhalten.';
      const pendingSources = get()._pendingSources;
      const pendingTraceSteps = get()._pendingTraceSteps;
      const pendingImages = get()._pendingImages;

      // Determine which sources are cited in the answer text (1-based [[N]])
      const citedIndices = new Set<number>();
      for (const m of normalizeCitationGroups(finalText).matchAll(CITATION_RE)) {
        citedIndices.add(parseInt(m[1], 10));
      }

      // Build graph from cited sources only (or all sources if none cited)
      const sourcesToShow = citedIndices.size > 0
        ? [...citedIndices].sort((a, b) => a - b).map((n) => pendingSources[n - 1]).filter(Boolean)
        : pendingSources.slice(0, 8);

      const questionNode: SourceNode = {
        id: 'q',
        label: question,
        type: 'question',
        score: 1.0,
      };
      const citedSourceNodes: SourceNode[] = sourcesToShow.map((src) => ({
        id: src.uid,
        label: src.name,
        type: classifySource(src),
        score: src.confidence ?? 0.5,
        source: src.source_ref ?? undefined,
        detail: src.text ?? undefined,
        metadata: src.metadata ?? undefined,
      }));
      const citedEdges = citedSourceNodes.map((n) => ({ from: 'q', to: n.id }));

      const assistantMsg: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        text: finalText,
        sourceCount,
        ...(pendingSources.length > 0 && { sources: pendingSources }),
        ...(pendingImages.length > 0 && { images: pendingImages }),
        timestamp: new Date(),
        ...(pendingTraceSteps.length > 0 && { debugTrace: pendingTraceSteps }),
        ...(doneMetadata?.trace_id ? { langfuseTraceId: doneMetadata.trace_id } : {}),
        ...(doneMetadata?.observation_id ? { langfuseObservationId: doneMetadata.observation_id } : {}),
        ...(streamPiiRedacted && { piiRedacted: true }),
        ...(dlpAction && { dlpAction }),
      };

      const finalGraphData: GraphData = {
        nodes: [questionNode, ...citedSourceNodes],
        edges: citedEdges,
      };

      set((s) => ({
        messages: s.messages.map((message) => (
          message.id === assistantMessageId ? assistantMsg : message
        )),
        graphData: finalGraphData,
        isLoading: false,
        _abortController: null,
        _pendingTraceSteps: [],
        _pendingSources: [],
      }));

      // Fire-and-forget: persist the Q&A turn to the server
      const userMsg = get().messages[get().messages.length - 2]; // the user message before assistant
      if (userMsg) {
        autoSaveMessages(userMsg, assistantMsg, finalGraphData, get, (partial) => set(partial));
      }

    } catch (err) {
      // ── Abort (user pressed Stop) — commit whatever partial answer arrived ─
      if (err instanceof Error && err.name === 'AbortError') {
        const partial = accText.trim();
        const pendingSources = get()._pendingSources;
        const pendingImages = get()._pendingImages;
        const pendingTraceSteps = get()._pendingTraceSteps;
        const partialMsg: ChatMessage = {
          id: assistantMessageId,
          role: 'assistant',
          text: partial || '_(Antwort abgebrochen)_',
          sourceCount,
          ...(pendingSources.length > 0 && { sources: pendingSources }),
          ...(pendingImages.length > 0 && { images: pendingImages }),
          timestamp: new Date(),
          ...(pendingTraceSteps.length > 0 && { debugTrace: pendingTraceSteps }),
        };
        set((s) => ({
          messages: s.messages.map((message) => (
            message.id === assistantMessageId ? partialMsg : message
          )),
          isLoading: false,
          _abortController: null,
          _pendingTraceSteps: [],
          _pendingSources: [],
        }));
        return;
      }

      // ── Network / server error ─────────────────────────────────────────
      // If a workspace is active, show a real error instead of misleading demo data.
      // Only fall back to demo when truly running without a workspace (pure demo mode).
      const hasWorkspace = !!useWorkspaceStore.getState().activeWorkspace;
      logger.warn('retrieval', 'Request failed', err);

      if (hasWorkspace) {
        // Real workspace — surface the error clearly, don't inject fake data
        const errorText =
          'Die Anfrage konnte nicht verarbeitet werden. ' +
          'Bitte prüfe die Verbindung zum Server und versuche es erneut.';
        set((s) => ({
          messages: s.messages.map((message) => (
            message.id === assistantMessageId
              ? {
              id: assistantMessageId,
              role: 'assistant',
              text: errorText,
              sourceCount: 0,
              timestamp: new Date(),
            }
              : message
          )),
          isLoading: false,
          _abortController: null,
        }));
      } else {
        // No workspace — demo fallback is appropriate
        await new Promise<void>((r) => setTimeout(r, 600));
        const demo = matchDemoResponse(question);
        set((s) => ({
          messages: [
            ...s.messages,
            {
              id: makeId(),
              role: 'assistant',
              text: demo.answer,
              sourceCount: demo.nodes.filter((n) => n.type !== 'question').length,
              timestamp: new Date(),
            },
          ],
          graphData: { nodes: demo.nodes, edges: demo.edges },
          isLoading: false,
          _abortController: null,
        }));
      }
    }
  },

  stopStreaming: () => {
    const ctrl = get()._abortController;
    if (ctrl) {
      ctrl.abort();
      set({ _abortController: null });
    }
  },

  setFilter: (filter) => set({ filter }),

  selectNode: (id) => {
    if (!id) {
      set({ selectedNodeId: null });
      return;
    }

    set({ selectedNodeId: id });
  },

  openSourceInline: (source) => {
    const target = resolveSourceTarget(source);
    if (!target) {
      set({ selectedNodeId: source.uid });
      return;
    }
    const prev = get().sourcePanel.target;
    if (prev?.documentId === target.documentId) {
      // Same document: update navigation target only (no document re-fetch)
      set({ sourcePanel: { mode: 'source', target } });
      return;
    }
    set({ sourcePanel: { mode: 'source', target } });
  },

  closeSourceInline: () => set({ sourcePanel: { mode: 'graph', target: null } }),

  drillDown: async (uid: string) => {
    const workspace = useWorkspaceStore.getState().activeWorkspace;
    const wsParam = workspace ? `?workspace_id=${encodeURIComponent(workspace.id)}` : '';
    try {
      const res = await fetch(
        `/api/python/retrieval/entity/${encodeURIComponent(uid)}/neighborhood${wsParam}`,
        { headers: await withAuthHeaders() },
      );
      if (!res.ok) return;
      const data = (await res.json()) as {
        nodes: Array<{ id: string; label: string; type?: string | null; score?: number; detail?: string | null; metadata?: Record<string, unknown> | null }>;
        edges: Array<{ from: string; to: string; label?: string | null }>;
      };
      set((s) => {
        const existingIds = new Set(s.graphData.nodes.map((n) => n.id));
        const newNodes: SourceNode[] = data.nodes
          .filter((n) => !existingIds.has(n.id))
          .map((n) => ({
            id: n.id,
            label: n.label,
            type: classifySource({ source_kind: 'entity', type: n.type ?? undefined }),
            score: n.score ?? 0.5,
            detail: n.detail ?? undefined,
            metadata: n.metadata ?? undefined,
          }));
        const newEdges = data.edges.map((e) => ({
          from: e.from,
          to: e.to,
          label: e.label ?? undefined,
        }));
        return {
          graphData: {
            nodes: [...s.graphData.nodes, ...newNodes],
            edges: [...s.graphData.edges, ...newEdges],
          },
        };
      });
    } catch (err) {
      logger.warn('retrieval', 'Drill-down failed', err);
    }
  },

  toggleDebugMode: () => set((s) => ({ debugMode: TALK_TO_DATA_DEBUG_ENABLED ? !s.debugMode : false })),

  // 0103.07.a — Dismiss-Aktionen für Security-Warnungen
  dismissInjectionWarning: () => set({ injectionWarning: null }),
  dismissRetrievalBlocked: () => set({ retrievalBlocked: null }),

  reset: () =>
    set({
      messages: [],
      graphData: { nodes: [], edges: [] },
      selectedNodeId: null,
      filter: 'all',
      isLoading: false,
      _pendingTraceSteps: [],
      _pendingSources: [],
      _pendingImages: [],
      sessionId: null,
      noteSaveStates: {},
      rightSidebarView: 'notes-list',
      sourcePanel: { mode: 'graph', target: null },
      machineContext: null,
      injectionWarning: null,
      retrievalBlocked: null,
    }),

  setMachineContext: (context) => set({ machineContext: context }),

  setRetrievalMode: (mode) => set({ retrievalMode: mode }),

  setSessionContextRetention: (retained) => {
    if (!retained) {
      set({ sessionContextRetention: false, machineContext: null });
    } else {
      set({ sessionContextRetention: true });
    }
  },

  clearMachineContext: async () => {
    const activeSessionId = get().sessionId;
    set({ machineContext: null });

    if (!activeSessionId) {
      return;
    }

    try {
      await clearMachineContextForSession(activeSessionId);
    } catch (err) {
      logger.warn('retrieval', 'clearMachineContext failed', err);
    }
  },

  handleMachineSelection: async (option: ClarificationCardOption, sessionId?: string) => {
    // ── Flache Auswahl (DevPlan 0145): Modell ODER Serie ────────────────
    // Es gibt keine mehrstufige Hersteller→Serie→Modell-Kaskade mehr. Eine
    // Serien-Option scoped nur auf `machine_series` (kein finales Modell, kein
    // Machine-Context); eine Modell-Option ist die finale Auswahl.
    const isSeries = option.cascade_type === 'series';

    if (!isSeries) {
      // Finale Modell-Auswahl — Machine-Context setzen.
      const nextMachineContext = { machineId: option.id, machineName: option.title };
      set({ machineContext: nextMachineContext });
    }

    // Letzte Nutzerfrage ermitteln um sie erneut mit Machine-Filter zu senden
    const { messages, sessionId: storeSessionId } = get();
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMsg) return;

    const workspace = useWorkspaceStore.getState().activeWorkspace;
    if (!workspace) return;

    const effectiveSessionId = sessionId ?? storeSessionId ?? undefined;
    const debugMode = TALK_TO_DATA_DEBUG_ENABLED ? get().debugMode : false;

    if (!isSeries && effectiveSessionId) {
      const nextMachineContext = { machineId: option.id, machineName: option.title };
      try {
        await persistMachineContextForSession(effectiveSessionId, nextMachineContext);
      } catch (err) {
        logger.warn('retrieval', 'persistMachineContext failed', err);
      }
    }

    const conversationHistory = messages
      .filter((m) => m.role !== 'system' && m.message_type !== 'clarification_cards')
      .slice(-10)
      .map((m) => ({ role: m.role, text: m.text }));

    // Flacher Filter (DevPlan 0145): Serie → machine_series; sonst → machine_id.
    const filters: Record<string, string> = isSeries
      ? { machine_series: option.id }
      : { machine_id: option.id, machine_name: option.title };

    const body: RetrievalApiRequest = {
      question: lastUserMsg.text,
      workspace_id: workspace.id,
      domain_id: workspace.domain?.id,
      conversation_history: conversationHistory,
      workspace_role: getCurrentUserRole(),
      ...(debugMode && { debug: true }),
      ...(effectiveSessionId && { session_id: effectiveSessionId }),
      filters,
    };

    set({ isLoading: true });
    try {
      const response = await fetch(RETRIEVAL_STREAM_URL, {
        method: 'POST',
        headers: await withAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      let accText = '';
      let sourceCount = 0;
      let machinePiiRedacted = false;
      let machineDlpAction: 'redacted' | 'blocked' | undefined;
      set({ _pendingTraceSteps: [], _pendingSources: [], _pendingImages: [] });

      for await (const event of parseSSE(response.body)) {
        if (event.type === 'token') {
          accText += event.content;
        } else if (event.type === 'answer_replace') {
          // Progressive Streaming + nachgelagerte DLP — Rohtext ersetzen.
          accText = event.content;
        } else if (event.type === 'sources') {
          sourceCount = event.sources.length;
          set({ _pendingSources: event.sources });
        } else if (event.type === 'images') {
          set({ _pendingImages: event.images });
        } else if (event.type === 'pii') {
          if (event.pii_redacted) machinePiiRedacted = true;
        } else if (event.type === 'dlp_violation') {
          // DLP output filter violation event (0101.07)
          machineDlpAction = event.action_taken;
        } else if (event.type === 'debug_trace') {
          set((s) => ({ _pendingTraceSteps: [...s._pendingTraceSteps, event.step] }));
        } else if (event.type === 'clarification') {
          // Folge-Rückfrage (DevPlan 0145). Flaches Modell — identisch zum
          // Haupt-`query`-Pfad via gemeinsamem Builder (kein Kaskaden-Text,
          // presentation Default 'cards', cascade_type undefined). Vor T9/T11
          // trug dieser Pfad noch die alte Kaskaden-Logik (Finding frontend-1).
          const clarificationMsg = buildClarificationMessage(event, effectiveSessionId);
          set((s) => ({
            messages: [...s.messages, clarificationMsg],
            isLoading: false,
            _abortController: null,
          }));
          return;
        } else if (event.type === 'error') {
          logger.warn('retrieval', 'Machine-selection SSE error', event.message);
        } else if (event.type === 'machine_context') {
          // Backend auto-selected a machine during cascade — update frontend context
          const ctx = { machineId: event.machine_id, machineName: event.machine_name };
          set({ machineContext: ctx });
          if (effectiveSessionId) {
            persistMachineContextForSession(effectiveSessionId, ctx).catch(() => {});
          }
        }
      }

      const finalText = accText.trim() || 'Keine Antwort erhalten.';
      const pendingSources = get()._pendingSources;
      const pendingImages = get()._pendingImages;
      const pendingTraceSteps = get()._pendingTraceSteps;

      const assistantMsg: ChatMessage = {
        id: makeId(),
        role: 'assistant',
        text: finalText,
        sourceCount,
        ...(pendingSources.length > 0 && { sources: pendingSources }),
        ...(pendingImages.length > 0 && { images: pendingImages }),
        timestamp: new Date(),
        ...(pendingTraceSteps.length > 0 && { debugTrace: pendingTraceSteps }),
        ...(machinePiiRedacted && { piiRedacted: true }),
        ...(machineDlpAction && { dlpAction: machineDlpAction }),
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        isLoading: false,
        _abortController: null,
        _pendingTraceSteps: [],
        _pendingSources: [],
      }));
    } catch (err) {
      logger.warn('retrieval', 'handleMachineSelection failed', err);
      set({ isLoading: false });
    }
  },

  // ── Session actions (server-backed) ───────────────────────────────────────

  bindWorkspace: (workspaceId: string | null) => {
    if (get().boundWorkspaceId === workspaceId) {
      return;
    }

    set({
      boundWorkspaceId: workspaceId,
      messages: [],
      graphData: { nodes: [], edges: [] },
      selectedNodeId: null,
      filter: 'all',
      isLoading: false,
      _abortController: null,
      _pendingTraceSteps: [],
      _pendingSources: [],
      _pendingImages: [],
      sessionId: null,
      sessions: [],
      isRestoring: false,
      isUndoing: false,
      notes: [],
      selectedNoteId: null,
      selectedNote: null,
      notesLoading: false,
      noteSaveStates: {},
      rightSidebarView: 'notes-list',
      sourcePanel: { mode: 'graph', target: null },
      machineContext: null,
    });
  },

  loadSessionList: async (workspaceId: string) => {
    try {
      const res = await fetchProtectedDotNet(`/api/dotnet/chatsessions/sessions?workspaceId=${encodeURIComponent(workspaceId)}`);
      if (!res) return;
      if (!res.ok) { logger.warn('chat', 'loadSessionList failed', res.status); return; }
      const data = await res.json() as Array<{
        id: string; title: string; messageCount: number;
        createdAt: string; updatedAt: string;
      }>;
      set({ sessions: data });
    } catch (err) { logger.warn('chat', 'loadSessionList error', err); }
  },

  loadNotes: async (workspaceId: string) => {
    const userId = getCurrentUserId();
    if (!userId) {
      set({
        notes: [],
        selectedNoteId: null,
        selectedNote: null,
        notesLoading: false,
        rightSidebarView: 'notes-list',
      });
      return;
    }

    set({ notesLoading: true });
    try {
      const res = await fetchProtectedDotNet(`${TALK_TO_DATA_NOTES_API}?workspaceId=${encodeURIComponent(workspaceId)}&userId=${encodeURIComponent(userId)}`);
      if (!res) {
        set({ notesLoading: false });
        return;
      }
      if (!res.ok) {
        logger.warn('notes', 'loadNotes failed', res.status);
        set({ notesLoading: false });
        return;
      }

      const notes = await res.json() as TalkToDataNoteSummary[];
      set((state) => ({
        notes,
        notesLoading: false,
        ...(state.selectedNoteId && !notes.some((note) => note.id === state.selectedNoteId)
          ? {
              selectedNoteId: null,
              selectedNote: null,
              rightSidebarView: 'notes-list' as RightSidebarView,
            }
          : {}),
      }));
    } catch (err) {
      logger.warn('notes', 'loadNotes error', err);
      set({ notesLoading: false });
    }
  },

  saveNoteFromMessage: async (messageId: string) => {
    const workspace = useWorkspaceStore.getState().activeWorkspace;
    const userId = getCurrentUserId();
    if (!workspace || !userId) return;

    const { messages, sessionId } = get();
    const messageIndex = messages.findIndex((message) => message.id === messageId);
    const message = messageIndex >= 0 ? messages[messageIndex] : null;
    if (!message || message.role !== 'assistant') return;

    const userMessage = [...messages.slice(0, messageIndex)].reverse().find((entry) => entry.role === 'user');
    if (!userMessage) return;

    set((state) => ({
      noteSaveStates: {
        ...state.noteSaveStates,
        [messageId]: 'saving',
      },
    }));

    try {
      const response = await fetchProtectedDotNet(TALK_TO_DATA_NOTES_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspaceId: workspace.id,
          userId,
          title: userMessage.text.length > 120 ? `${userMessage.text.slice(0, 117)}...` : userMessage.text,
          questionText: userMessage.text,
          answerMarkdown: message.text,
          sources: message.sources ?? [],
          citations: extractCitationIndices(message.text),
          images: message.images ?? [],
          sessionId: toOptionalGuid(sessionId),
          sourceMessageId: toOptionalGuid(message.id),
        }),
      });
      if (!response) {
        throw new Error('Missing access token');
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const created = await response.json() as TalkToDataNoteDetail;
      const summary = buildNoteSummary(created);

      set((state) => ({
        notes: [summary, ...state.notes.filter((note) => note.id !== summary.id)],
        noteSaveStates: {
          ...state.noteSaveStates,
          [messageId]: 'saved',
        },
      }));
      toast.success('Notiz gespeichert');
    } catch (err) {
      logger.warn('notes', 'saveNoteFromMessage error', err);
      set((state) => ({
        noteSaveStates: {
          ...state.noteSaveStates,
          [messageId]: 'error',
        },
      }));
      toast.error('Notiz konnte nicht gespeichert werden');
    }
  },

  selectNote: async (noteId: string) => {
    const workspace = useWorkspaceStore.getState().activeWorkspace;
    const userId = getCurrentUserId();
    if (!workspace || !userId) return;

    set({ notesLoading: true, selectedNodeId: null });
    try {
      const res = await fetchProtectedDotNet(`${TALK_TO_DATA_NOTES_API}/${encodeURIComponent(noteId)}?workspaceId=${encodeURIComponent(workspace.id)}&userId=${encodeURIComponent(userId)}`);
      if (!res) {
        set({ notesLoading: false });
        return;
      }
      if (!res.ok) {
        logger.warn('notes', 'selectNote failed', res.status);
        set({ notesLoading: false });
        return;
      }

      const note = await res.json() as TalkToDataNoteDetail;
      set({
        selectedNoteId: note.id,
        selectedNote: note,
        notesLoading: false,
        rightSidebarView: 'note-detail',
      });
    } catch (err) {
      logger.warn('notes', 'selectNote error', err);
      set({ notesLoading: false });
    }
  },

  closeNoteDetail: () => set({
    selectedNoteId: null,
    selectedNote: null,
    selectedNodeId: null,
    rightSidebarView: 'notes-list',
  }),

  deleteNote: async (noteId: string) => {
    const workspace = useWorkspaceStore.getState().activeWorkspace;
    const userId = getCurrentUserId();
    if (!workspace || !userId) return;

    try {
      const response = await fetchProtectedDotNet(`${TALK_TO_DATA_NOTES_API}/${encodeURIComponent(noteId)}?workspaceId=${encodeURIComponent(workspace.id)}&userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      });
      if (!response) {
        toast.error('Notiz konnte nicht gelöscht werden');
        return;
      }
      if (!response.ok && response.status !== 404) {
        logger.warn('notes', 'deleteNote failed', response.status);
        toast.error('Notiz konnte nicht gelöscht werden');
        return;
      }
    } catch (err) {
      logger.warn('notes', 'deleteNote error', err);
      toast.error('Notiz konnte nicht gelöscht werden');
      return;
    }

    set((state) => ({
      notes: state.notes.filter((note) => note.id !== noteId),
      ...(state.selectedNoteId === noteId
        ? {
            selectedNoteId: null,
            selectedNote: null,
            selectedNodeId: null,
            rightSidebarView: 'notes-list' as RightSidebarView,
          }
        : {}),
    }));
    toast.success('Notiz gelöscht');
  },

  loadSession: async (sessionId: string) => {
    try {
      const res = await fetchProtectedDotNet(`/api/dotnet/chatsessions/sessions/${sessionId}`);
      if (!res) return;
      if (!res.ok) { logger.warn('chat', 'loadSession failed', res.status); return; }
      const data = await res.json() as {
        id: string; title: string;
        messages: Array<{
          id: string; role: string; content: string; sourceCount: number;
          sourcesJson: string | null; graphDataJson: string | null;
          debugTraceJson: string | null; imagesJson: string | null; createdAt: string;
          langfuseTraceId?: string | null; langfuseObservationId?: string | null;
          feedbackValue?: 'up' | 'down' | null; feedbackSubmittedAt?: string | null;
        }>;
      };
      // Hydrate ChatMessage[] from server DTOs
      const messages: ChatMessage[] = data.messages.map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        text: m.content,
        sourceCount: m.sourceCount || undefined,
        sources: m.sourcesJson ? JSON.parse(m.sourcesJson) : undefined,
        images: m.imagesJson ? JSON.parse(m.imagesJson) : undefined,
        timestamp: new Date(m.createdAt),
        debugTrace: m.debugTraceJson ? JSON.parse(m.debugTraceJson) : undefined,
        langfuseTraceId: m.langfuseTraceId ?? undefined,
        langfuseObservationId: m.langfuseObservationId ?? undefined,
        feedbackValue: m.feedbackValue ?? undefined,
        feedbackState: m.feedbackValue ? 'submitted' : undefined,
        feedbackSubmittedAt: m.feedbackSubmittedAt ? new Date(m.feedbackSubmittedAt) : undefined,
      }));
      // Rebuild graph from last assistant message's graphData
      const lastAssistant = [...data.messages].reverse().find((m) => m.role === 'assistant');
      const graphData: GraphData = lastAssistant?.graphDataJson
        ? JSON.parse(lastAssistant.graphDataJson)
        : { nodes: [], edges: [] };
      const machineContext = await loadMachineContextForSession(sessionId);

      set({
        sessionId: data.id,
        messages,
        graphData,
        machineContext,
        selectedNodeId: null,
        filter: 'all',
        isLoading: false,
        _pendingTraceSteps: [],
        _pendingSources: [],
        _pendingImages: [],
        rightSidebarView: get().selectedNote ? 'note-detail' : 'notes-list',
        sourcePanel: { mode: 'graph', target: null },
      });
    } catch (err) { logger.warn('chat', 'loadSession error', err); }
  },

  newSession: () => {
    set({
      messages: [],
      graphData: { nodes: [], edges: [] },
      selectedNodeId: null,
      filter: 'all',
      isLoading: false,
      _pendingTraceSteps: [],
      _pendingSources: [],
      _pendingImages: [],
      sessionId: null,
      machineContext: null,
      isRestoring: false,
      isUndoing: false,
      noteSaveStates: {},
      rightSidebarView: get().selectedNote ? 'note-detail' : 'notes-list',
      sourcePanel: { mode: 'graph', target: null },
    });
  },

  deleteSession: async (sessionId: string) => {
    try {
      await fetchProtectedDotNet(`/api/dotnet/chatsessions/sessions/${sessionId}`, { method: 'DELETE' });
    } catch (err) { logger.warn('chat', 'deleteSession error', err); }
    set((s) => ({
      sessions: s.sessions.filter((sess) => sess.id !== sessionId),
      ...(s.sessionId === sessionId && {
        sessionId: null,
        machineContext: null,
        messages: [],
        graphData: { nodes: [], edges: [] },
        selectedNodeId: null,
        noteSaveStates: {},
        rightSidebarView: s.selectedNote ? 'note-detail' : 'notes-list',
        sourcePanel: { mode: 'graph' as const, target: null },
      }),
    }));
  },

  restoreCheckpoint: async (messageId: string) => {
    set({ isRestoring: true });
    try {
      const { messages, sessionId } = get();
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;

      // Server-side truncation (if session is persisted)
      if (sessionId) {
        try {
          await fetchProtectedDotNet(
            `/api/dotnet/chatsessions/sessions/${sessionId}/messages/restore/${messageId}`,
            { method: 'DELETE' },
          );
        } catch (err) { logger.warn('chat', 'restoreCheckpoint server error', err); }
      }

      // Local truncation
      const kept = messages.slice(0, idx + 1);

      // Rebuild graph from the last assistant message in kept
      const lastAssistant = [...kept].reverse().find((m) => m.role === 'assistant');
      const graphData: GraphData = lastAssistant?.sources
        ? rebuildGraphFromSources(lastAssistant.text, lastAssistant.sources)
        : { nodes: [], edges: [] };

      set({
        messages: kept,
        graphData,
        selectedNodeId: null,
        rightSidebarView: get().selectedNote ? 'note-detail' : 'notes-list',
        sourcePanel: { mode: 'graph', target: null },
      });
    } finally {
      set({ isRestoring: false });
    }
  },

  undoLastTurn: async () => {
    set({ isUndoing: true });
    try {
      const { messages, sessionId } = get();
      if (messages.length < 2) return;

      // Find last assistant message
      const lastAssistantIdx = [...messages].reverse().findIndex((m) => m.role === 'assistant');
      if (lastAssistantIdx < 0) return;
      const absIdx = messages.length - 1 - lastAssistantIdx;
      const userIdx = absIdx - 1;

      // Server-side removal
      if (sessionId) {
        try {
          await fetchProtectedDotNet(
            `/api/dotnet/chatsessions/sessions/${sessionId}/messages/undo-last-turn`,
            { method: 'DELETE' },
          );
        } catch (err) { logger.warn('chat', 'undoLastTurn server error', err); }
      }

      // Local removal
      const kept = messages.filter((_, i) => i !== userIdx && i !== absIdx);

      // Rebuild graph from the new last assistant message
      const lastAssistant = [...kept].reverse().find((m) => m.role === 'assistant');
      const graphData: GraphData = lastAssistant?.sources
        ? rebuildGraphFromSources(lastAssistant.text, lastAssistant.sources)
        : { nodes: [], edges: [] };

      set({
        messages: kept,
        graphData,
        selectedNodeId: null,
        rightSidebarView: get().selectedNote ? 'note-detail' : 'notes-list',
        sourcePanel: { mode: 'graph', target: null },
      });
    } finally {
      set({ isUndoing: false });
    }
  },

  submitMessageFeedback: async (messageId, value) => {
    const targetMessage = get().messages.find((message) => message.id === messageId && message.role === 'assistant');
    if (!targetMessage?.langfuseTraceId) {
      return;
    }

    if (targetMessage.feedbackState === 'submitting' || targetMessage.feedbackState === 'submitted') {
      return;
    }

    const sessionId = get().sessionId;
    if (!sessionId) {
      toast.info('Antwort wird noch gespeichert. Bitte in einem Moment erneut bewerten.');
      return;
    }

    set((state) => ({
      messages: updateMessageFeedbackState(state.messages, messageId, value, 'submitting'),
    }));

    try {
      const scoreResponse = await fetchProtectedDotNet(LANGFUSE_SCORE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          traceId: targetMessage.langfuseTraceId,
          observationId: targetMessage.langfuseObservationId ?? null,
          value: value === 'up',
        }),
      });
      if (!scoreResponse) {
        throw new Error('score:missing-token');
      }

      if (!scoreResponse.ok) {
        throw new Error(`score:${scoreResponse.status}`);
      }

      const submittedAt = new Date();
      const feedbackResponse = await fetchProtectedDotNet(
        `${CHAT_SESSION_API}/sessions/${sessionId}/messages/${messageId}/feedback`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value, submittedAt: submittedAt.toISOString() }),
        },
      );
      if (!feedbackResponse) {
        throw new Error('feedback:missing-token');
      }

      if (!feedbackResponse.ok) {
        throw new Error(`feedback:${feedbackResponse.status}`);
      }

      set((state) => ({
        messages: updateMessageFeedbackState(state.messages, messageId, value, 'submitted', submittedAt),
      }));
    } catch (err) {
      logger.warn('chat', 'submitMessageFeedback failed', err);
      set((state) => ({
        messages: updateMessageFeedbackState(state.messages, messageId, value, 'error'),
      }));
      toast.error('Feedback konnte nicht gespeichert werden');
    }
  },
}), {
  name: 'sophograph-chat-session',
  storage: sessionStorageAdapter,
  partialize: (state) => ({
    messages: state.messages,
    graphData: state.graphData,
    boundWorkspaceId: state.boundWorkspaceId,
    sessionId: state.sessionId,
    debugMode: TALK_TO_DATA_DEBUG_ENABLED ? state.debugMode : false,
    machineContext: state.machineContext,
    retrievalMode: state.retrievalMode,
    sessionContextRetention: state.sessionContextRetention,
  }),
}));